'use client';

import { useEffect, useMemo, useState } from 'react';

import Image from 'next/image';

import { useTranslations } from 'next-intl';
import { z } from 'zod';

import { api } from '~/trpc/client';

type Provider = 'clerk' | 'cognito';

type EmailGateProps = {
  onProvider: (provider: Provider, email: string) => void;
};

function isValidEmail(value: string) {
  return z.string().email().safeParse(value).success;
}

export const EmailGate = ({ onProvider }: EmailGateProps) => {
  const t = useTranslations('auth.signIn');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const emailValid = useMemo(() => isValidEmail(email), [email]);

  const {
    data: provider,
    isLoading,
    isError,
  } = api.login.getAuthProvider.useQuery(
    { email },
    { enabled: submitted && emailValid },
  );

  useEffect(() => {
    if (isError) {
      setSubmitted(false);
    }
  }, [isError]);

  useEffect(() => {
    if (submitted && provider) {
      onProvider(provider, email);
    }
  }, [submitted, provider, email, onProvider]);

  const canSubmit = emailValid && !isLoading && !submitted;

  function handleContinue() {
    if (!emailValid) return;
    setSubmitted(true);
  }

  return (
    <main className="grid h-screen w-screen place-items-center">
      <div className="w-full max-w-sm space-y-4 rounded-xl border border-gray-200 p-8 shadow-sm">
        <div className="flex justify-center">
          <Image
            alt="Nsight Logo"
            height={75}
            priority
            src="/nsight-logo.png"
            width={75}
          />
        </div>
        <h1 className="text-xl font-semibold text-gray-900">
          {t('gate.title')}
        </h1>
        <div className="space-y-2">
          <label
            className="block text-sm font-medium text-gray-700"
            htmlFor="email"
          >
            {t('gate.emailLabel')}
          </label>
          <input
            autoComplete="email"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            id="email"
            onChange={(e) => {
              setEmail(e.target.value);
              setSubmitted(false);
            }}
            placeholder={t('gate.emailPlaceholder')}
            type="email"
            value={email}
          />
        </div>
        <button
          className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!canSubmit}
          onClick={handleContinue}
          type="button"
        >
          {isLoading ? t('gate.loading') : t('gate.continueButton')}
        </button>
      </div>
    </main>
  );
};
